"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { Button } from "./Button";
import { Typography } from "./Typography";

export interface CTAProps {
  title: string;
  subtitle?: string;
  footnote?: string;
  primaryButton: {
    text: string;
    onClick?: () => void;
    href?: string;
  };
  secondaryButton?: {
    text: string;
    onClick?: () => void;
    href?: string;
  };
  variant?: "default" | "light" | "fullGradient";
  stats?: Array<{
    value: string;
    label: string;
  }>;
  className?: string;
}

const CTA: React.FC<CTAProps> = ({
  title,
  subtitle,
  footnote,
  primaryButton,
  secondaryButton,
  variant = "default",
  stats,
  className,
}) => {
  const isLight = variant === "light";
  const isFullGradient = variant === "fullGradient";

  const primaryVariant = isLight || isFullGradient ? "primary" : "outlinePrimary";
  const secondaryVariant = isLight
    ? "secondary"
    : isFullGradient
      ? "outlinePrimary"
      : "outlineSecondary";

  return (
    <section
      className={cn(
        "cta-section",
        isLight && "py-16 lg:py-20 bg-gradient-to-r from-red-50 to-orange-50",
        isFullGradient &&
          "py-16 lg:py-24 bg-gradient-to-r from-[#1D2C7F] to-[#1D2C7F] text-white",
        className
      )}
    >
      <div className="cta-content">
        <Typography
          variant="h2"
          className={cn(
            "cta-title",
            isLight && "text-text-primary",
            isFullGradient && "text-white"
          )}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant="large"
            className={cn(
              "cta-subtitle",
              isLight ? "text-gray-600" : isFullGradient ? "text-white/90" : "text-gray-200"
            )}
          >
            {subtitle}
          </Typography>
        )}

        <div className="cta-buttons">
          {primaryButton.href ? (
            <a href={primaryButton.href}>
              <Button variant={primaryVariant} asChild>
                {primaryButton.text}
              </Button>
            </a>
          ) : (
            <Button variant={primaryVariant} onClick={primaryButton.onClick}>
              {primaryButton.text}
            </Button>
          )}

          {secondaryButton &&
            (secondaryButton.href ? (
              <a href={secondaryButton.href}>
                <Button variant={secondaryVariant} asChild>
                  {secondaryButton.text}
                </Button>
              </a>
            ) : (
              <Button variant={secondaryVariant} onClick={secondaryButton.onClick}>
                {secondaryButton.text}
              </Button>
            ))}
        </div>

        {stats && (
          <div className="cta-stats">
            {stats.map((stat, index) => (
              <div key={index} className="cta-stat">
                <Typography
                  variant="h3"
                  className={cn(
                    "cta-stat-number",
                    isLight ? "text-text-primary" : "text-white"
                  )}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="small"
                  className={cn(
                    "cta-stat-label",
                    isLight ? "text-gray-600" : "text-gray-200"
                  )}
                >
                  {stat.label}
                </Typography>
              </div>
            ))}
          </div>
        )}

        {/* Optional footnote text, useful for trust badges / certifications */}
        {footnote && (
          <Typography
            variant="small"
            className={cn(
              "mt-4 text-xs md:text-sm",
              isLight ? "text-gray-500" : "text-white/75"
            )}
          >
            {footnote}
          </Typography>
        )}
      </div>
    </section>
  );
};

export { CTA };
